import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { FaSearch, FaTimesCircle } from "react-icons/fa";
import { toast } from "react-toastify";
import styles from "./Sales.module.css";
import WebService from "../Services/WebService";

export type BulkAction = "CONFIRM_ESTIMATE" | "ACCEPT";

/** Estimates waiting for client confirmation. */
export const ESTIMATE_STATUSES = ["CHECKOUT", "PENDING_PAYMENT"];
/** Confirmed orders waiting for admin acceptance. */
export const PLACED_STATUSES = ["PLACED", "PAYMENT_SUBMITTED"];

export const isEstimateStatus = (status?: string) =>
  ESTIMATE_STATUSES.includes(String(status || "").toUpperCase());
export const isPlacedStatus = (status?: string) =>
  PLACED_STATUSES.includes(String(status || "").toUpperCase());

interface Employee {
  user_id: number;
  username: string;
  mobile_number: string;
  [key: string]: any;
}

interface BulkResult {
  sale_order_id: number;
  success: boolean;
  status?: string;
  message?: string;
}

interface Props {
  action: BulkAction;
  saleOrderIds: Array<number | string>;
  onClose: () => void;
  /** Called after the request finishes (even partially) so callers can refresh. */
  onDone: () => void;
}

const PAYMENT_METHODS = [
  { value: "CREDIT", label: "Credit (collect payment later)" },
  { value: "CASH", label: "Cash (full due amount)" },
  { value: "UPI", label: "UPI (full due amount)" },
  { value: "BANK_TRANSFER", label: "Bank Transfer (full due amount)" },
];

const BulkConfirmModal: React.FC<Props> = ({ action, saleOrderIds, onClose, onDone }) => {
  const [paymentMethod, setPaymentMethod] = useState("CREDIT");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [employeeSearch, setEmployeeSearch] = useState("");
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  const count = saleOrderIds.length;
  const isAccept = action === "ACCEPT";

  useEffect(() => {
    if (!isAccept) return;
    const id = setTimeout(async () => {
      setEmployeeLoading(true);
      try {
        const params = new URLSearchParams({ page_number: "1", limit: "100" });
        if (employeeSearch.trim()) params.set("name", employeeSearch.trim());
        const res: any = await WebService.getAPI({
          action: `/v1/door-management/employees?${params.toString()}`,
        });
        setEmployees(Array.isArray(res?.data?.employees) ? res.data.employees : []);
      } catch {
        setEmployees([]);
      } finally {
        setEmployeeLoading(false);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [employeeSearch, isAccept]);

  const handleSubmit = async () => {
    if (isAccept && !selectedEmployee) {
      toast.error("Please select an employee.");
      return;
    }
    setSubmitting(true);
    try {
      const body: Record<string, any> = {
        sale_order_ids: saleOrderIds.map(Number),
        action,
        notes: notes || undefined,
      };
      if (isAccept) {
        body.assigned_to = selectedEmployee!.user_id;
      } else {
        body.payment_method = paymentMethod;
        if (reference.trim()) body.transaction_id = reference.trim();
      }
      const res: any = await WebService.postAPI({
        action: `/v1/door-management/saleorders/bulk-confirm`,
        body,
      });
      const data = res?.data || {};
      const results: BulkResult[] = Array.isArray(data.results) ? data.results : [];
      const failed = results.filter((r) => !r.success);
      const succeeded = data.succeeded ?? results.length - failed.length;
      const verb = isAccept ? "accepted" : "confirmed";
      if (succeeded > 0) {
        toast.success(
          isAccept
            ? `${succeeded} order(s) accepted and assigned to ${selectedEmployee?.username}. Set the delivery date to move them to production.`
            : `${succeeded} estimate(s) confirmed. They are now Placed and ready to accept.`
        );
      }
      failed.forEach((r) =>
        toast.error(`Order #${r.sale_order_id} not ${verb}: ${r.message || "failed"}`, { autoClose: 8000 })
      );
      onDone();
      onClose();
    } catch {
      // WebService already shows the error toast
    } finally {
      setSubmitting(false);
    }
  };

  return ReactDOM.createPortal(
    <div className={styles.overlay}>
      <div className={styles.assignModal}>
        <h2>{isAccept ? `Accept ${count} order(s)` : `Confirm ${count} estimate(s)`}</h2>
        <div style={{ color: "#666", fontSize: 13, marginBottom: 10 }}>
          Orders: {saleOrderIds.map((id) => `#${id}`).join(", ")}
        </div>

        {isAccept ? (
          <div className={styles.modalField}>
            <label>Assign Employee</label>
            {selectedEmployee ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
                <span
                  style={{
                    background: "#fff8cc",
                    border: "1px solid #ffe08a",
                    borderRadius: 8,
                    padding: "8px 12px",
                    fontWeight: 600,
                  }}
                >
                  {selectedEmployee.username}{" "}
                  <span style={{ color: "#777", fontWeight: 400 }}>{selectedEmployee.mobile_number}</span>
                </span>
                <FaTimesCircle
                  title="Clear"
                  onClick={() => setSelectedEmployee(null)}
                  style={{ color: "#e43e2d", fontSize: 20, cursor: "pointer" }}
                />
              </div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <FaSearch style={{ color: "#999" }} />
                  <input
                    type="text"
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    placeholder="Type name or mobile..."
                    style={{ flex: 1, padding: "7px 8px", borderRadius: 7, border: "1px solid #ddd" }}
                    autoFocus
                  />
                </div>
                <div className={styles.employeeDropdown} style={{ marginTop: 8 }}>
                  {employeeLoading ? (
                    <div style={{ color: "#b4b4b4", padding: 12, textAlign: "center" }}>Loading employees...</div>
                  ) : employees.length === 0 ? (
                    <div style={{ color: "#b4b4b4", padding: 12, textAlign: "center" }}>No employees found</div>
                  ) : (
                    employees.map((emp) => (
                      <div
                        key={emp.user_id}
                        className={styles.employeeOption}
                        onClick={() => setSelectedEmployee(emp)}
                      >
                        {emp.username} &nbsp;
                        <span style={{ color: "#888", fontWeight: 400 }}>{emp.mobile_number}</span>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <div className={styles.modalField}>
              <label>Payment Method</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{ width: "100%", padding: "8px", borderRadius: 7, border: "1px solid #ddd" }}
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            {paymentMethod !== "CREDIT" && (
              <div className={styles.modalField}>
                <label>Reference / Transaction ID (optional)</label>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  style={{ width: "100%", padding: "7px 8px", borderRadius: 7, border: "1px solid #ddd" }}
                />
              </div>
            )}
          </>
        )}

        <div className={styles.modalField}>
          <label>Note (optional)</label>
          <textarea
            className={styles.modalTextarea}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={250}
          />
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.primaryActionBtn}
            onClick={handleSubmit}
            disabled={submitting || count === 0 || (isAccept && !selectedEmployee)}
          >
            {submitting ? "Processing..." : isAccept ? "Assign & Accept" : "Confirm Order(s)"}
          </button>
          <button className={styles.secondaryBtn} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BulkConfirmModal;
